import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';

import * as Styled from './ReceiptTable.styles';
import { selectors } from '../../reducer';
import { CurrentReceiptItem } from '../../types';
import { distributeClientBonus } from '../../helpers';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 14,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
  },
}));

const ReceiptTable = ({
  currentReceipt,
  showWithBonuses,
}: {
  currentReceipt: CurrentReceiptItem[];
  showWithBonuses: boolean;
}) => {
  const { t } = useTranslation();
  const clientInfo = useSelector(selectors.clientInfo);
  const clientInfoBalance = (clientInfo && clientInfo.balance) ?? 0;

  const rows = useMemo(
    () =>
      distributeClientBonus(
        currentReceipt,
        showWithBonuses ? clientInfoBalance : 0
      ),
    [currentReceipt, clientInfoBalance, showWithBonuses]
  );

  return (
    <Styled.ReceiptTableContainer>
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 550, tableLayout: 'fixed' }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell width="5%" align="center">
                №
              </StyledTableCell>
              <StyledTableCell width="45%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_NAME')}
              </StyledTableCell>
              <StyledTableCell width="10%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_COUNT')}
              </StyledTableCell>
              <StyledTableCell width="10%" align="center">
                {t('LoyaltyPage.Table.PRODUCT_PRICE')}
              </StyledTableCell>
              <StyledTableCell width="15%" align="center">
                {t('LoyaltyPage.Table.SUM')}
              </StyledTableCell>
              <StyledTableCell width="15%" align="center">
                {t('LoyaltyPage.Table.DISCOUNT_TYPE')}
              </StyledTableCell>
              <StyledTableCell width="15%" align="center">
                {t('LoyaltyPage.Table.BONUS')}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.sku}>
                <StyledTableCell width="10px" align="center" padding="none">
                  {index + 1}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.name}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.quantity}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.base_price.toFixed(2)} ₸
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.appliedBonus > 0 && (
                    <span
                      style={
                        row.appliedBonus > 0
                          ? {
                              textDecoration: 'line-through',
                              marginRight: '5px',
                            }
                          : {}
                      }
                    >
                      {row.total_price}
                    </span>
                  )}
                  {row.total_price > 0 && (
                    <span>
                      {Math.round(row.total_price - row.appliedBonus)} ₸
                    </span>
                  )}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  - {row.appliedBonus > 0 ? `${row.appliedBonus} ₸` : ''}
                </StyledTableCell>
                <StyledTableCell width="60px" align="center" padding="none">
                  {row.bonusesToClient} Б
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Styled.ReceiptTableContainer>
  );
};

export default ReceiptTable;
